import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, fmtDate } from "@/lib/api";
import { Trash, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ParkedSalesDialog({ open, onClose, onResume, onChange }) {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/sales/parked");
            setList(data);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
    }, [open]);

    const remove = async (id) => {
        try {
            await api.delete(`/sales/parked/${id}`);
            await load();
            onChange?.();
            toast.success("Διαγράφηκε");
        } catch {
            toast.error("Σφάλμα διαγραφής");
        }
    };

    const resume = async (sale) => {
        const ok = await onResume(sale);
        if (ok !== false) {
            // Delete from server only after successful hydrate
            try {
                await api.delete(`/sales/parked/${sale.id}`);
            } catch {
                // ignore
            }
            onChange?.();
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="rounded-none max-w-2xl border-2 border-[#0A0A0A]"
                data-testid="parked-sales-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
                        ΠΩΛΗΣΕΙΣ ΣΕ ΑΝΑΣΤΟΛΗ
                    </DialogTitle>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto -mx-6">
                    {loading ? (
                        <div className="p-8 text-center text-sm text-[#5C5F66]">
                            Φόρτωση...
                        </div>
                    ) : list.length === 0 ? (
                        <div className="p-8 text-center text-sm text-[#5C5F66]">
                            Δεν υπάρχουν πωλήσεις σε αναστολή.
                        </div>
                    ) : (
                        <div className="divide-y divide-[#E2E4E9]">
                            {list.map((p) => {
                                const total = p.line_items.reduce(
                                    (s, l) => s + l.unit_price * l.quantity,
                                    0
                                );
                                return (
                                    <div
                                        key={p.id}
                                        className="px-6 py-4 flex items-start gap-3 hover:bg-[#F4F5F7]"
                                        data-testid={`parked-row-${p.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-heading font-black text-base tracking-tighter">
                                                    {p.customer_name || "ΧΩΡΙΣ ΠΕΛΑΤΗ"}
                                                </span>
                                                {p.customer_code && (
                                                    <span className="text-xs font-mono bg-[#0A0A0A] text-white px-2 py-0.5">
                                                        #{p.customer_code}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-[#5C5F66] mt-1">
                                                {p.line_items.length} είδη ·{" "}
                                                <span className="font-bold text-[#002FA7]">
                                                    {total.toFixed(2).replace(".", ",")} €
                                                </span>{" "}
                                                · {fmtDate(p.created_at)}
                                            </div>
                                            {p.note && (
                                                <div className="text-[11px] text-[#7A5C00] bg-[#FFF8DD] inline-block px-2 py-0.5 mt-1">
                                                    {p.note}
                                                </div>
                                            )}
                                        </div>
                                        <Button
                                            onClick={() => resume(p)}
                                            data-testid={`resume-${p.id}`}
                                            className="rounded-none h-10 bg-[#002FA7] hover:bg-[#001D66] text-white font-bold"
                                        >
                                            <ArrowCounterClockwise
                                                size={14}
                                                weight="bold"
                                                className="mr-1"
                                            />
                                            ΕΠΑΝΑΦΟΡΑ
                                        </Button>
                                        <Button
                                            onClick={() => remove(p.id)}
                                            variant="outline"
                                            className="rounded-none h-10 border-[#FF3B30] text-[#FF3B30]"
                                        >
                                            <Trash size={14} weight="bold" />
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
